<template>
  <div class="quill">
    <div :id="toolbarId">
      <div class="ql-formats">
        <button class="ql-bold"></button>
        <button class="ql-italic"></button>
        <button class="ql-underline"></button>
        <button class="ql-link"></button>
        <button class="ql-blockquote"></button>
        <button class="ql-code"></button>
        <button class="ql-image"></button>
        <button type="button" class="ql-list" value="ordered"></button>
        <button type="button" class="ql-list" value="bullet"></button>
      </div>
    </div>
    <div :id="editorId" :name="name" class="" ref="editor"></div>
  </div>
</template>
<script>
export default {
  name: "html-editor",
  props: {
    value: {
      type: String,
      default: "",
    },
    name: String,
    disabled: Boolean,
  },
  data() {
    return {
      editor: null,
      content: null,
      lastHtmlValue: "",
      editorId: null,
      toolbarId: null,
      originalHtml: "",
    };
  },
  methods: {
    initialize(Quill) {
      // Create a custom format for preserving classes
      const Inline = Quill.import('blots/inline');
      const Block = Quill.import('blots/block');
      
      class ClassBlot extends Inline {
        static create(value) {
          let node = super.create();
          if (value) {
            node.setAttribute('class', value);
          }
          return node;
        }
        
        static formats(node) {
          return node.getAttribute('class');
        }
      }
      ClassBlot.blotName = 'class';
      ClassBlot.tagName = 'span';
      ClassBlot.className = 'ql-class';
      
      class ClassBlockBlot extends Block {
        static create(value) {
          let node = super.create();
          if (value) {
            node.setAttribute('class', value);
          }
          return node;
        }
        
        static formats(node) {
          return node.getAttribute('class');
        }
      }
      ClassBlockBlot.blotName = 'class-block';
      ClassBlockBlot.tagName = 'div';
      
      Quill.register(ClassBlot);
      Quill.register(ClassBlockBlot);

      this.editor = new Quill(`#${this.editorId}`, {
        theme: "snow",
        modules: {
          toolbar: `#${this.toolbarId}`,
          clipboard: {
            matchVisual: false,
            matchers: [
              ['*', (node, delta) => {
                // Preserve classes on all elements
                if (node.nodeType === Node.ELEMENT_NODE && node.className) {
                  const className = node.className;
                  delta.ops = delta.ops.map(op => {
                    if (op.insert && typeof op.insert === 'string') {
                      return {
                        ...op,
                        attributes: {
                          ...op.attributes,
                          'class': className
                        }
                      };
                    }
                    return op;
                  });
                }
                return delta;
              }]
            ]
          }
        },
        formats: ['bold', 'italic', 'underline', 'link', 'blockquote', 'code', 'image', 'list', 'class', 'class-block']
      });

      if (this.value?.length > 0) {
        // Set content and then manually restore HTML structure
        this.setContentWithClasses(this.value);
      }

      let editorRef = this.$refs.editor;
      let node = editorRef.children[0];
      this.editor.on("text-change", () => {
        let html = node.innerHTML;
        if (html === "<p><br></p>") {
          html = "";
        }
        this.content = html;
        this.$emit("input", this.content);
      });
      if (this.disabled) {
        this.editor.disable();
      } else {
        this.editor.enable();
      }
    },
    setContentWithClasses(htmlContent) {
      if (!this.editor || !htmlContent) {
        return;
      }
      
      // Clear the editor first
      this.editor.setContents([]);
      
      // Use dangerouslyPasteHTML which preserves more of the original HTML structure
      this.editor.clipboard.dangerouslyPasteHTML(0, htmlContent);
      
      // Alternative approach: directly set the innerHTML if the above doesn't work
      this.$nextTick(() => {
        const editorContent = this.$refs.editor.querySelector('.ql-editor');
        if (editorContent && htmlContent) {
          // Temporarily disable the editor to prevent text-change events
          const wasEnabled = this.editor.isEnabled();
          this.editor.disable();
          
          // Set the HTML content directly
          editorContent.innerHTML = htmlContent;
          
          // Re-enable if it was enabled before
          if (wasEnabled) {
            this.editor.enable();
          }
          
          // Update the internal content
          this.content = htmlContent;
        }
      });
    },
    pasteHTML() {
      if (!this.editor) {
        return;
      }
      // Use the new method that preserves classes
      this.setContentWithClasses(this.value);
    },
    randomString() {
      let text = "";
      let possible = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz";

      for (let i = 0; i < 5; i++)
        text += possible.charAt(Math.floor(Math.random() * possible.length));

      return text;
    },
  },
  async mounted() {
    let Quill = await import("quill");
    Quill = Quill.default || Quill;
    this.editorId = this.randomString();
    this.toolbarId = this.randomString();
    this.$nextTick(() => {
      this.initialize(Quill);
    });
  },
  watch: {
    value(newVal) {
      if (newVal !== this.content) {
        this.pasteHTML(newVal);
      }
    },
  },
};
</script>